import React from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { ArrowLeft, Home, User, LogOut } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';

const Header = ({ showBack = true, backTo = null, title = null }) => {
  const { isAuthenticated, user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const getBackPath = () => {
    if (backTo) return backTo;
    
    // Use browser history to go back to the previous page
    // This is more intuitive than hardcoded paths
    return -1;
  };

  const getPageTitle = () => {
    if (title) return title;
    
    const path = location.pathname;
    if (path.startsWith('/stage1')) return 'Stage 1 Application';
    if (path.startsWith('/stage2')) return 'Stage 2 Application';
    if (path.startsWith('/payment')) return 'Payment';
    if (path.startsWith('/application-review')) return 'Application Review';
    if (path.startsWith('/dashboard')) return 'Dashboard';
    return 'Academic City';
  };

  return (
    <header className="bg-white shadow-sm border-b border-gray-200 sticky top-0 z-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16">
          {/* Left Section - Logo and Back Button */}
          <div className="flex items-center space-x-4">
            {/* Logo */}
            <Link to="/" className="flex items-center space-x-3">
              <img 
                src="/images/Logos/tac-logo.png" 
                alt="Academic City Logo" 
                className="h-10 w-auto"
              />
            </Link>

            {/* Back Button */}
            {showBack && isAuthenticated && (
              <button
                onClick={() => {
                  const backPath = getBackPath();
                  if (backPath === -1) {
                    navigate(-1); // Go back in browser history
                  } else {
                    navigate(backPath); // Navigate to specific path
                  }
                }}
                className="flex items-center space-x-2 text-gray-600 hover:text-teal-600 px-3 py-2 rounded-md text-sm font-medium transition-colors"
              >
                <ArrowLeft className="h-4 w-4" />
                <span>Back</span>
              </button>
            )}
          </div>

          {/* Center Section - Page Title */}
          {title && (
            <div className="flex-1 flex justify-center">
              <h1 className="text-lg font-semibold text-gray-900 text-center">
                {getPageTitle()}
              </h1>
            </div>
          )}

          {/* Right Section - Navigation and User Menu */}
          <div className="flex items-center space-x-4">
            {/* Home Button */}
            {isAuthenticated && (
              <Link
                to="/dashboard"
                className="flex items-center space-x-2 text-gray-600 hover:text-teal-600 px-3 py-2 rounded-md text-sm font-medium transition-colors"
              >
                <Home className="h-4 w-4" />
                <span className="hidden sm:inline">Home</span>
              </Link>
            )}

            {/* User Menu */}
            {isAuthenticated && user ? (
              <div className="relative group">
                <button className="flex items-center space-x-2 text-gray-600 hover:text-teal-600 px-3 py-2 rounded-md text-sm font-medium transition-colors">
                  <User className="h-4 w-4" />
                  <span className="hidden sm:inline">
                    {user.name || user.phone || 'User'}
                  </span>
                </button>
                
                {/* Dropdown Menu */}
                <div className="absolute right-0 mt-2 w-48 bg-white rounded-md shadow-lg py-1 z-50 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200">
                  <div className="px-4 py-2 text-sm text-gray-700 border-b border-gray-100">
                    <p className="font-medium">{user.name || 'User'}</p>
                    <p className="text-gray-500">{user.phone || user.email || 'No contact info'}</p>
                  </div>
                  
                  <Link
                    to="/dashboard"
                    className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 transition-colors"
                  >
                    Dashboard
                  </Link>
                  
                  <button
                    onClick={handleLogout}
                    className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 transition-colors"
                  >
                    Sign Out
                  </button>
                </div>
              </div>
            ) : (
              /* Show Login/Register links when not authenticated */
              <div className="flex items-center space-x-4">
                <Link
                  to="/login"
                  className="bg-teal-600 hover:bg-teal-700 text-white px-4 py-2 rounded-md text-sm font-medium transition-colors shadow-md hover:shadow-lg"
                >
                  Login
                </Link>
                {/* <Link
                  to="/register"
                  className="bg-teal-600 hover:bg-teal-700 text-white px-4 py-2 rounded-md text-sm font-medium transition-colors"
                >
                  Register
                </Link> */}
              </div>
            )}
          </div>
        </div>
      </div>
    </header>
  );
};

export default Header;
